import { useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconLogout } from '@/components/icons';
import { initialsOf } from '@/lib/shellPrefs';

/**
 * Top-bar account button: initials (plus name and role from `sm` up) that open a small menu with
 * the full name, the role and "Cerrar sesión". Closes on a click outside or Escape (focus goes
 * back to the button), and the item is reachable by keyboard.
 */
export function UserMenu({
  name,
  roleLabel,
  onSignOut,
}: {
  name: string;
  roleLabel: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    itemRef.current?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Cuenta de ${name}`}
        className="flex h-10 items-center gap-2 rounded-md px-1.5 text-left transition-colors hover:bg-surface-muted sm:px-2"
      >
        <span
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-brand-primary"
          aria-hidden="true"
        >
          {initialsOf(name)}
        </span>
        <span className="hidden min-w-0 sm:block">
          <span className="block max-w-40 truncate text-sm font-medium leading-tight text-text-primary">{name}</span>
          <span className="block text-xs leading-tight text-text-muted">{roleLabel}</span>
        </span>
        <IconChevronDown
          className={`hidden h-4 w-4 text-text-muted transition-transform duration-150 sm:block ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-semibold text-text-primary">{name}</p>
            <p className="text-xs text-text-muted">{roleLabel}</p>
          </div>
          <button
            ref={itemRef}
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="flex min-h-11 w-full items-center gap-2 px-4 text-sm font-medium text-text-primary transition-colors hover:bg-surface-muted focus-visible:bg-surface-muted"
          >
            <IconLogout />
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  );
}
